import type { ProductTaxonomyType } from '@carrefour-gcs/shared';
import { useQuery } from '@tanstack/vue-query';
import { computed, unref, type Ref } from 'vue';

import { type FiltersTreeType } from '#composables/kpisPerimeter';
import { filterToTreeViewItemWithDefaultValue } from '#domain/core/filter/filter.mapper';
import { listUserFilters } from '#domain/core/filter/filter.query';

interface UseTriptyqueDataProps {
  debouncedSearch: Readonly<Ref<string>>;
  enabled: Ref<boolean>;
  checkedItemsState: Ref<FiltersTreeType>;
  taxonomy: Ref<ProductTaxonomyType>;
}

export function useTriptyqueData({ debouncedSearch, enabled, checkedItemsState, taxonomy }: UseTriptyqueDataProps) {
  const {
    data: defaultTriptyqueData,
    isLoading: isLoadingDefaultValue,
    isFetching: isFetchingDefaultValue
  } = useQuery({
    enabled,
    queryKey: [listUserFilters.name, taxonomy],
    queryFn: async () =>
      listUserFilters({
        filterType: 'brandType',
        search: '',
        limit: 100,
        taxonomy: unref(taxonomy)
      }),
    select: (result) => {
      if (result.status === 'error') {
        return undefined;
      }

      return result.data;
    }
  });

  const {
    data: triptyqueData,
    isLoading,
    isFetching
  } = useQuery({
    enabled,
    queryKey: [listUserFilters.name, debouncedSearch, taxonomy],
    queryFn: async () =>
      listUserFilters({
        filterType: 'brandType',
        search: debouncedSearch.value,
        limit: 100,
        taxonomy: unref(taxonomy)
      }),
    select: (result) => {
      if (result.status === 'error') {
        return undefined;
      }

      return result.data;
    }
  });

  const itemsTriptyque = computed(() => {
    if (!triptyqueData.value || !defaultTriptyqueData.value) {
      return undefined;
    }

    return filterToTreeViewItemWithDefaultValue({
      filter: triptyqueData.value,
      defaultFilters: defaultTriptyqueData.value,
      selectedFilters: checkedItemsState.value.triptyqueFilters
    });
  });

  const isLoadingTriptyque = computed(() => {
    // on tanstack query v4, there is a [known issue](https://github.com/TanStack/query/issues/3975) that
    // isLoading and isStale stay true if `enabled` is false.
    return (isLoading.value && isFetching.value) || (isLoadingDefaultValue.value && isFetchingDefaultValue.value);
  });

  return {
    itemsTriptyque,
    isLoadingTriptyque
  };
}
